// src/pages/common/MyTasksPage.js
import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { settings } from "../../helpers/settings";
import authHeader from "../../helpers/functions/auth-header";
import axios from "axios";
import Lottie from "lottie-react";
import { toast } from "../../helpers/swal";
import loadingAnim from "../../helpers/animations/loading-pulse.json";
import confettiAnim from "../../helpers/animations/confetti.json";
import taskCompleteAnim from "../../helpers/animations/task-complete.json";
import writingAnim from "../../helpers/animations/writing.json";
import "../../assets/css/MyTasksPage.css";
import Sidebar from "../../components/common/sidebar/sidebar";

const MyTasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [listings, setListings] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [meetingNotes, setMeetingNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Meeting notes state'leri
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [meetingForm, setMeetingForm] = useState({
    title: "",
    content: "",
    is_important: false,
  });

  const stats = {
    total: tasks.length,
    pending: tasks.filter((t) => t.status === "pending").length,
    inProgress: tasks.filter((t) => t.status === "in_progress").length,
    completed: tasks.filter((t) => t.status === "completed").length,
    urgent: tasks.filter((t) => t.priority === "urgent").length,
    unreadNotifications: notifications.filter((n) => !n.is_read).length,
  };

  // Data fetching
  useEffect(() => {
    fetchAllData();
    checkAdminStatus();
  }, []);

  const checkAdminStatus = () => {
    const userEmail = localStorage.getItem("email");
    setIsAdmin(userEmail === "mhmtmnctl20@gmail.com");
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchTasks(),
        fetchWeeklyListings(),
        fetchNotifications(),
        fetchMeetingNotes(),
      ]);
    } catch (error) {
      toast("Error loading data", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_user_tasks.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setTasks(response.data.tasks);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  const fetchWeeklyListings = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_weekly_listings.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setListings(response.data.listings);
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_task_notifications.php`,
        {
          headers: authHeader(),
        }
      );
      if (response.data.success) {
        setNotifications(response.data.notifications);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  const fetchMeetingNotes = async () => {
    try {
      const response = await axios.get(
        `${settings.apiURL}/get_meeting_notes.php`,
        { headers: authHeader() }
      );
      if (response.data.success) {
        setMeetingNotes(response.data.notes);
      }
    } catch (error) {
      console.error("Error fetching meeting notes:", error);
    }
  };

  // Drag & Drop handler
  const onDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;

    if (source.droppableId === destination.droppableId) return;

    const taskId = parseInt(draggableId);
    const newStatus = destination.droppableId;

    // Optimistic update
    const updatedTasks = tasks.map((task) =>
      task.id === taskId ? { ...task, status: newStatus } : task
    );
    setTasks(updatedTasks);

    // API call
    try {
      const response = await axios.post(
        `${settings.apiURL}/update_task_status.php`,
        {
          task_id: taskId,
          status: newStatus,
        },
        { headers: authHeader() }
      );

      if (!response.data.success) {
        // Revert on error
        fetchTasks();
        toast("Failed to update task status", "error");
      } else if (newStatus === "completed") {
        // Confetti for completed tasks!
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        toast("Task completed! 🎉", "success");
      }
    } catch (error) {
      fetchTasks(); // Revert on error
      toast("Network error", "error");
    }
  };

  // Add new listing - UPDATE DESTEKLİ VERSİYON
  // Add new listing - UPDATE DESTEKLİ VERSİYON
  const handleListingSave = async (day, listing, index) => {
    try {
      const payload = {
        week_day: day,
        listing_url: listing.listing_url,
        listing_title: listing.listing_title || `Listing ${index + 1}`,
      };

      // Eğer listing'in id'si varsa, bu bir update işlemidir
      if (listing.id) {
        payload.id = listing.id; // ID'yi de payload'a ekle
      }

      // OPTIMISTIC UPDATE: Hemen state'i güncelle
      setListings((prevListings) => {
        const updatedListings = { ...prevListings };
        if (!updatedListings[day]) {
          updatedListings[day] = Array(10)
            .fill()
            .map(() => ({
              id: null,
              listing_url: "",
              listing_title: "",
              status: "pending_review",
              admin_notes: "",
            }));
        }

        const dayListings = [...updatedListings[day]];
        dayListings[index] = {
          ...dayListings[index],
          ...payload,
          status: "pending_review",
        };

        return {
          ...updatedListings,
          [day]: dayListings,
        };
      });

      // ID'ye göre farklı endpoint kullan
      const endpoint = listing.id
        ? `${settings.apiURL}/update_weekly_listing.php` // UPDATE
        : `${settings.apiURL}/add_weekly_listing.php`; // ADD

      const response = await axios.post(endpoint, payload, {
        headers: authHeader(),
      });

      console.log(
        `✅ ${listing.id ? "Update" : "Add"} response:`,
        response.data
      );

      if (response.data.success) {
        toast(
          `Listing ${index + 1} ${listing.id ? "updated" : "saved"}!`,
          "success"
        );
        return true;
      } else {
        // Hata durumunda eski state'e dön
        fetchWeeklyListings();
        toast(
          response.data.message ||
            `Failed to ${listing.id ? "update" : "save"} listing`,
          "error"
        );
        return false;
      }
    } catch (error) {
      // Hata durumunda eski state'e dön
      fetchWeeklyListings();
      console.error(
        `Error ${listing.id ? "updating" : "saving"} listing:`,
        error
      );
      toast(`Failed to ${listing.id ? "update" : "save"} listing`, "error");
      return false;
    }
  };
  // Mark notification as read
  const markNotificationRead = async (notificationId = null) => {
    try {
      await axios.post(
        `${settings.apiURL}/mark_notification_read.php`,
        { notification_id: notificationId },
        { headers: authHeader() }
      );
      fetchNotifications();
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  // Meeting notes functions
  const handleAddMeetingNote = async () => {
    if (!meetingForm.title.trim() || !meetingForm.content.trim()) {
      toast("Title and content are required", "warning");
      return;
    }

    try {
      const url = editingNote
        ? `${settings.apiURL}/update_meeting_note.php`
        : `${settings.apiURL}/add_meeting_note.php`;

      const payload = editingNote
        ? { ...meetingForm, note_id: editingNote.id }
        : meetingForm;

      const response = await axios.post(url, payload, {
        headers: authHeader(),
      });

      if (response.data.success) {
        toast(editingNote ? "Note updated!" : "Note added!", "success");
        setMeetingForm({ title: "", content: "", is_important: false });
        setShowMeetingForm(false);
        setEditingNote(null);
        fetchMeetingNotes();
      }
    } catch (error) {
      console.error("Error saving meeting note:", error);
      toast("Failed to save note", "error");
    }
  };

  const handleDeleteMeetingNote = async (noteId) => {
    try {
      const response = await axios.post(
        `${settings.apiURL}/delete_meeting_note.php`,
        { note_id: noteId },
        { headers: authHeader() }
      );

      if (response.data.success) {
        toast("Note deleted!", "success");
        fetchMeetingNotes();
      }
    } catch (error) {
      console.error("Error deleting meeting note:", error);
      toast("Failed to delete note", "error");
    }
  };

  const handleEditMeetingNote = (note) => {
    setEditingNote(note);
    setMeetingForm({
      title: note.title,
      content: note.content,
      is_important: note.is_important,
    });
    setShowMeetingForm(true);
  };

  const cancelMeetingForm = () => {
    setMeetingForm({ title: "", content: "", is_important: false });
    setShowMeetingForm(false);
    setEditingNote(null);
  };

  const groupedTasks = {
    pending: tasks.filter((task) => task.status === "pending"),
    in_progress: tasks.filter((task) => task.status === "in_progress"),
    completed: tasks.filter((task) => task.status === "completed"),
  };

  const weekDays = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  if (loading) {
    return (
      <div className="mytasks-loading-container">
        <div className="mytasks-loading-content">
          <div className="mytasks-lottie-container">
            <Lottie animationData={loadingAnim} loop={true} />
          </div>
          <p className="mytasks-loading-text">Loading your tasks...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />

      <div
        className="mytasks-container"
        style={{ flex: 1, marginLeft: "250px" }}
      >
        {/* Confetti Animation */}
        {showConfetti && (
          <div className="mytasks-confetti-overlay">
            <Lottie animationData={confettiAnim} loop={false} />
          </div>
        )}

        <div className="mytasks-content">
          <div style={{ marginBottom: "20px" }}>
            <button
              onClick={() => (window.location.href = "/home")}
              className="mytasks-home-button"
            >
              ← Home
            </button>
          </div>

          {/* Header */}
          <header className="mytasks-header">
            <h1 className="mytasks-title">My Tasks</h1>
            <p className="mytasks-subtitle">
              Manage your tasks and weekly listings
            </p>
          </header>

          {/* Stats Dashboard */}
          <div className="mytasks-stats-grid">
            <StatCard
              icon="📊"
              title="Total"
              value={stats.total}
              color="blue"
            />
            <StatCard
              icon="⏳"
              title="To Do"
              value={stats.pending}
              color="gray"
            />
            <StatCard
              icon="🔄"
              title="In Progress"
              value={stats.inProgress}
              color="yellow"
            />
            <StatCard
              icon="✅"
              title="Completed"
              value={stats.completed}
              color="green"
            />
            <StatCard
              icon="🔔"
              title="Notifications"
              value={stats.unreadNotifications}
              color="purple"
            />
          </div>

          <div className="mytasks-main-layout">
            {/* Kanban Board - Üstte */}
            <div className="mytasks-kanban-section">
              <div className="mytasks-card">
                <h2 className="mytasks-section-title">
                  <span>Task Board</span>
                  <div className="mytasks-writing-animation">
                    <Lottie animationData={writingAnim} loop={true} />
                  </div>
                </h2>

                <div className="mytasks-kanban-with-notes">
                  <DragDropContext onDragEnd={onDragEnd}>
                    <div className="mytasks-kanban-grid">
                      {["pending", "in_progress", "completed"].map((status) => (
                        <TaskColumn
                          key={status}
                          status={status}
                          tasks={groupedTasks[status]}
                        />
                      ))}

                      {/* YENİ: Toplantı Notları Column */}
                      <MeetingNotesColumn
                        notes={meetingNotes}
                        isAdmin={isAdmin}
                        onAdd={() => setShowMeetingForm(true)}
                        onEdit={handleEditMeetingNote}
                        onDelete={handleDeleteMeetingNote}
                      />
                    </div>
                  </DragDropContext>
                </div>
              </div>
            </div>

            {/* Notifications Panel */}
            <div className="mytasks-card">
              <div className="mytasks-notifications-header">
                <h2 className="mytasks-section-title">🔔 Notifications</h2>
                {notifications.length > 0 && (
                  <button
                    onClick={() => markNotificationRead()}
                    className="mytasks-mark-read-button"
                  >
                    Mark all as read
                  </button>
                )}
              </div>

              <div className="mytasks-notifications-list">
                {notifications.length === 0 ? (
                  <p className="mytasks-empty-state">No notifications</p>
                ) : (
                  notifications.map((notification) => (
                    <NotificationItem
                      key={notification.id}
                      notification={notification}
                      onMarkRead={markNotificationRead}
                    />
                  ))
                )}
              </div>
            </div>

            {/* Weekly Listings - YENİ TASARIM */}
            <div className="mytasks-listings-section-wide">
              <div className="mytasks-card">
                <h2 className="mytasks-section-title">📅 Weekly Listings</h2>

                {/* YENİ: 10 inputlu liste */}
                <div className="mytasks-listings-grid-wide">
                  {weekDays.map((day) => (
                    <DayListingsInputs
                      key={day}
                      day={day}
                      listings={listings[day] || []}
                      onListingSave={handleListingSave}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Meeting Notes Form Modal */}
      {showMeetingForm && (
        <div className="mytasks-modal-overlay">
          <div className="mytasks-modal">
            <h3>{editingNote ? "Edit Meeting Note" : "Add Meeting Note"}</h3>

            <div className="mytasks-form-group">
              <label>Title *</label>
              <input
                type="text"
                value={meetingForm.title}
                onChange={(e) =>
                  setMeetingForm({ ...meetingForm, title: e.target.value })
                }
                placeholder="Enter note title"
                className="mytasks-form-input"
              />
            </div>

            <div className="mytasks-form-group">
              <label>Content *</label>
              <textarea
                value={meetingForm.content}
                onChange={(e) =>
                  setMeetingForm({ ...meetingForm, content: e.target.value })
                }
                placeholder="Enter note content"
                rows="4"
                className="mytasks-form-textarea"
              />
            </div>

            <div className="mytasks-form-group">
              <label className="mytasks-checkbox-label">
                <input
                  type="checkbox"
                  checked={meetingForm.is_important}
                  onChange={(e) =>
                    setMeetingForm({
                      ...meetingForm,
                      is_important: e.target.checked,
                    })
                  }
                  className="mytasks-checkbox"
                />
                Important Note
              </label>
            </div>

            <div className="mytasks-modal-actions">
              <button
                onClick={cancelMeetingForm}
                className="mytasks-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMeetingNote}
                className="mytasks-primary-button"
              >
                {editingNote ? "Update Note" : "Add Note"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ icon, title, value, color }) => {
  const colorClasses = {
    blue: "mytasks-stat-card-blue",
    gray: "mytasks-stat-card-gray",
    yellow: "mytasks-stat-card-yellow",
    green: "mytasks-stat-card-green",
    purple: "mytasks-stat-card-purple",
  };

  return (
    <div className={`mytasks-stat-card ${colorClasses[color]}`}>
      <div className="mytasks-stat-content">
        <span className="mytasks-stat-icon">{icon}</span>
        <div className="mytasks-stat-text">
          <div className="mytasks-stat-value">{value}</div>
          <div className="mytasks-stat-label">{title}</div>
        </div>
      </div>
    </div>
  );
};

const TaskColumn = ({ status, tasks }) => {
  const statusConfig = {
    pending: {
      title: "To Do",
      class: "mytasks-column-pending",
    },
    in_progress: {
      title: "In Progress",
      class: "mytasks-column-in-progress",
    },
    completed: {
      title: "Completed",
      class: "mytasks-column-completed",
    },
  };

  const config = statusConfig[status];

  return (
    <div className="mytasks-column">
      <div className={`mytasks-column-header ${config.class}`}>
        {config.title}{" "}
        <span className="mytasks-column-count">({tasks.length})</span>
      </div>
      <Droppable droppableId={status}>
        {(provided) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className="mytasks-column-content"
          >
            {tasks.map((task, index) => (
              <TaskCard key={task.id} task={task} index={index} />
            ))}
            {provided.placeholder}
            {tasks.length === 0 && (
              <div className="mytasks-empty-column">No tasks</div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );
};

const TaskCard = ({ task, index }) => {
  const [showDetails, setShowDetails] = useState(false);

  return (
    <>
      <Draggable draggableId={task.id.toString()} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            className={`mytasks-task-card ${
              snapshot.isDragging ? "mytasks-task-card-dragging" : ""
            } ${
              task.priority === "urgent"
                ? "mytasks-task-priority-urgent"
                : task.priority === "high"
                ? "mytasks-task-priority-high"
                : task.priority === "medium"
                ? "mytasks-task-priority-medium"
                : "mytasks-task-priority-low"
            }`}
            onClick={() => setShowDetails(true)}
            style={{ cursor: "pointer" }} // Tıklanabilir olduğunu göster
          >
            <h3 className="mytasks-task-title">{task.title}</h3>
            {task.description && (
              <p className="mytasks-task-description">
                {task.description.length > 100
                  ? `${task.description.substring(0, 100)}...`
                  : task.description}
                {task.description.length > 100 && (
                  <span style={{ color: "#007bff", fontSize: "12px" }}>
                    {" "}
                    Devamını oku
                  </span>
                )}
              </p>
            )}
            <div className="mytasks-task-footer">
              <span
                className={`mytasks-task-priority ${
                  task.priority === "urgent"
                    ? "mytasks-priority-urgent"
                    : task.priority === "high"
                    ? "mytasks-priority-high"
                    : task.priority === "medium"
                    ? "mytasks-priority-medium"
                    : "mytasks-priority-low"
                }`}
              >
                {task.priority}
              </span>
              {task.due_date && (
                <span className="mytasks-task-due-date">
                  {new Date(task.due_date).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        )}
      </Draggable>

      {/* Task Detay Modal'ı */}
      {showDetails && (
        <TaskDetailsModal task={task} onClose={() => setShowDetails(false)} />
      )}
    </>
  );
};

const TaskDetailsModal = ({ task, onClose }) => {
  return (
    <div className="mytasks-modal-overlay">
      <div
        className="mytasks-modal"
        style={{ maxWidth: "600px", maxHeight: "80vh" }}
      >
        <div className="mytasks-modal-header">
          <h3>Task Details</h3>
          <button className="mytasks-close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="mytasks-modal-content">
          {/* Task Başlığı */}
          <div className="mytasks-detail-section">
            <label className="mytasks-detail-label">Title:</label>
            <div className="mytasks-detail-value">{task.title}</div>
          </div>

          {/* Task Açıklaması */}
          <div className="mytasks-detail-section">
            <label className="mytasks-detail-label">Description:</label>
            <div className="mytasks-detail-value mytasks-task-description-full">
              {task.description || "No description provided"}
            </div>
          </div>

          {/* Task Detayları */}
          <div className="mytasks-details-grid">
            <div className="mytasks-detail-item">
              <label className="mytasks-detail-label">Priority:</label>
              <span
                className={`mytasks-task-priority ${
                  task.priority === "urgent"
                    ? "mytasks-priority-urgent"
                    : task.priority === "high"
                    ? "mytasks-priority-high"
                    : task.priority === "medium"
                    ? "mytasks-priority-medium"
                    : "mytasks-priority-low"
                }`}
              >
                {task.priority}
              </span>
            </div>

            <div className="mytasks-detail-item">
              <label className="mytasks-detail-label">Status:</label>
              <span className="mytasks-detail-value">
                {task.status === "pending"
                  ? "To Do"
                  : task.status === "in_progress"
                  ? "In Progress"
                  : task.status === "completed"
                  ? "Completed"
                  : task.status}
              </span>
            </div>

            {task.due_date && (
              <div className="mytasks-detail-item">
                <label className="mytasks-detail-label">Due Date:</label>
                <span className="mytasks-detail-value">
                  {new Date(task.due_date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
            )}

            {task.created_at && (
              <div className="mytasks-detail-item">
                <label className="mytasks-detail-label">Created:</label>
                <span className="mytasks-detail-value">
                  {new Date(task.created_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {/* Eğer task'a atanmış bir user varsa */}
          {task.assigned_to && (
            <div className="mytasks-detail-section">
              <label className="mytasks-detail-label">Assigned To:</label>
              <div className="mytasks-detail-value">{task.assigned_to}</div>
            </div>
          )}
        </div>

        <div className="mytasks-modal-actions">
          <button className="mytasks-primary-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

const MeetingNotesColumn = ({ notes, isAdmin, onAdd, onEdit, onDelete }) => {
  return (
    <div className="mytasks-column">
      <div className="mytasks-column-header mytasks-column-meeting">
        📝 Meeting Notes
        {isAdmin && (
          <button onClick={onAdd} className="mytasks-add-note-button">
            +
          </button>
        )}
      </div>
      <div className="mytasks-column-content">
        {notes.length === 0 ? (
          <div className="mytasks-empty-column">
            {isAdmin ? "No meeting notes yet" : "No meeting notes"}
          </div>
        ) : (
          notes.map((note) => (
            <MeetingNoteCard
              key={note.id}
              note={note}
              isAdmin={isAdmin}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          ))
        )}
      </div>
    </div>
  );
};

const MeetingNoteCard = ({ note, isAdmin, onEdit, onDelete }) => {
  return (
    <div
      className={`mytasks-meeting-note ${note.is_important ? "important" : ""}`}
    >
      <div className="mytasks-meeting-note-header">
        <h4 className="mytasks-meeting-note-title">{note.title}</h4>
        {note.is_important && (
          <span className="mytasks-important-badge">⭐</span>
        )}
      </div>
      <div className="mytasks-meeting-note-content">
        {note.content.split("\n").map((line, index) => (
          <p key={index}>{line}</p>
        ))}
      </div>
      <div className="mytasks-meeting-note-footer">
        <span className="mytasks-meeting-note-date">
          {new Date(note.created_at).toLocaleDateString()}
        </span>
        {isAdmin && (
          <div className="mytasks-meeting-note-actions">
            <button
              onClick={() => onEdit(note)}
              className="mytasks-edit-button"
            >
              Edit
            </button>
            <button
              onClick={() => onDelete(note.id)}
              className="mytasks-delete-button"
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// DayListingsInputs Component - SADECE SAVE BUTONU İLE KAYIT
// DayListingsInputs Component - DEĞİŞİKLİK TAKİPLİ VERSİYON
const DayListingsInputs = ({ day, listings, onListingSave }) => {
  const [localListings, setLocalListings] = useState([]);
  const [originalListings, setOriginalListings] = useState([]); // YENİ: Orijinal hallerini sakla
  const [changedIndexes, setChangedIndexes] = useState(new Set());
  const [showRevisionModal, setShowRevisionModal] = useState({
    show: false,
    listing: null,
  });
  const [savingIndex, setSavingIndex] = useState(null);

  useEffect(() => {
    const filledListings = [...(listings || [])];

    while (filledListings.length < 10) {
      filledListings.push({
        id: null,
        listing_url: "",
        listing_title: "",
        status: "pending_review",
        admin_notes: "",
      });
    }

    const finalListings = filledListings.slice(0, 10);
    setLocalListings(finalListings);
    setOriginalListings(finalListings); // Orijinal hallerini de kaydet
  }, [day, listings]);

  const handleInputChange = (index, field, value) => {
    setLocalListings((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [field]: value,
        listing_title:
          field === "listing_url" &&
          value.trim() &&
          !updated[index].listing_title
            ? `Listing ${index + 1}`
            : updated[index].listing_title,
      };
      return updated;
    });

    // Değişiklik olup olmadığını kontrol et
    const originalValue = originalListings[index]?.[field] || "";
    const hasChanged = value !== originalValue;

    if (hasChanged) {
      setChangedIndexes((prev) => new Set(prev).add(index));
    } else {
      setChangedIndexes((prev) => {
        const newSet = new Set(prev);
        newSet.delete(index);
        return newSet;
      });
    }
  };

  const handleSave = async (index) => {
    if (savingIndex === index) {
      return;
    }

    const listing = localListings[index];
    const originalListing = originalListings[index];

    // Değişiklik yoksa veya URL boşsa kaydetme
    if (!listing.listing_url.trim() || !changedIndexes.has(index)) {
      return;
    }

    try {
      setSavingIndex(index);
      const success = await onListingSave(day, listing, index);

      if (success) {
        // Başarılı kayıt sonrası orijinal listeyi güncelle
        setOriginalListings((prev) => {
          const updated = [...prev];
          updated[index] = { ...listing };
          return updated;
        });

        setChangedIndexes((prev) => {
          const newSet = new Set(prev);
          newSet.delete(index);
          return newSet;
        });
      }
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setSavingIndex(null);
    }
  };

  const handleKeyPress = (index, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave(index);
    }
  };

  // YENİ: Input değerini sıfırlama fonksiyonu
  const handleReset = (index) => {
    const originalListing = originalListings[index];
    setLocalListings((prev) => {
      const updated = [...prev];
      updated[index] = { ...originalListing };
      return updated;
    });

    setChangedIndexes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(index);
      return newSet;
    });
  };

  const showRevisionReason = (listing) => {
    setShowRevisionModal({
      show: true,
      listing: listing,
    });
  };

  const closeRevisionModal = () => {
    setShowRevisionModal({
      show: false,
      listing: null,
    });
  };

  // YENİ: Değişiklik durumuna göre buton stilini belirle
  const getSaveButtonStyle = (index) => {
    const listing = localListings[index];
    const hasChanges = changedIndexes.has(index);
    const isEmpty = !listing.listing_url.trim();
    const isSaving = savingIndex === index;

    if (isSaving) {
      return "mytasks-save-button-saving";
    } else if (hasChanges && !isEmpty) {
      return "mytasks-save-button-active";
    } else {
      return "mytasks-save-button-inactive";
    }
  };

  return (
    <div className="mytasks-day-inputs-wide">
      <h4 className="mytasks-day-title-wide">
        {day.charAt(0).toUpperCase() + day.slice(1)}
      </h4>

      <div className="mytasks-inputs-list">
        {localListings.map((listing, index) => {
          const hasChanges = changedIndexes.has(index);
          const isEmpty = !listing.listing_url.trim();
          const isSaving = savingIndex === index;
          const canSave = hasChanges && !isEmpty && !isSaving;
          const canReset = hasChanges && !isSaving;

          return (
            <div key={index} className="mytasks-input-item">
              <span className="mytasks-input-number">[{index + 1}]</span>
              <input
                type="url"
                placeholder="Listing URL"
                value={listing.listing_url}
                onChange={(e) =>
                  handleInputChange(index, "listing_url", e.target.value)
                }
                onKeyPress={(e) => handleKeyPress(index, e)}
                className="mytasks-url-input"
                disabled={isSaving}
              />

              {/* Reset Butonu - Sadece değişiklik varsa göster */}
              {canReset && (
                <button
                  onClick={() => handleReset(index)}
                  className="mytasks-reset-button"
                  title="Reset changes"
                  disabled={isSaving}
                >
                  ↩️
                </button>
              )}

              <button
                onClick={() => handleSave(index)}
                disabled={!canSave}
                className={`mytasks-save-button ${getSaveButtonStyle(index)}`}
                title={
                  canSave
                    ? "Save changes"
                    : isEmpty
                    ? "URL cannot be empty"
                    : hasChanges
                    ? ""
                    : "No changes to save"
                }
              >
                {isSaving ? "⏳" : "💾"}
              </button>

              <div className={`mytasks-status-badge ${listing.status}`}>
                {listing.status === "approved"
                  ? "✅"
                  : listing.status === "needs_revision"
                  ? "⚠️"
                  : listing.status === "pending_review"
                  ? "⏳"
                  : "➕"}

                {listing.status === "needs_revision" && listing.admin_notes && (
                  <button
                    onClick={() => showRevisionReason(listing)}
                    className="mytasks-info-button"
                    title="View revision reason"
                  >
                    ℹ️
                  </button>
                )}
              </div>

              {/* Değişiklik göstergesi */}
              {hasChanges && (
                <span
                  className="mytasks-unsaved-indicator"
                  title="Unsaved changes"
                >
                  *
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Revision Reason Modal */}
      {showRevisionModal.show && (
        <div className="mytasks-modal-overlay">
          <div className="mytasks-modal" style={{ maxWidth: "500px" }}>
            <div className="mytasks-modal-header">
              <h3>Revision Required</h3>
              <button
                className="mytasks-close-button"
                onClick={closeRevisionModal}
              >
                ✕
              </button>
            </div>

            <div className="mytasks-modal-content">
              <div className="mytasks-detail-section">
                <label className="mytasks-detail-label">Listing URL:</label>
                <div className="mytasks-detail-value">
                  <a
                    href={showRevisionModal.listing.listing_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "#007bff", wordBreak: "break-all" }}
                  >
                    {showRevisionModal.listing.listing_url}
                  </a>
                </div>
              </div>

              <div className="mytasks-detail-section">
                <label className="mytasks-detail-label">Revision Reason:</label>
                <div
                  className="mytasks-detail-value mytasks-task-description-full"
                  style={{
                    background: "#fff3cd",
                    border: "1px solid #ffeaa7",
                    borderRadius: "4px",
                    padding: "12px",
                    color: "#856404",
                  }}
                >
                  {showRevisionModal.listing.admin_notes}
                </div>
              </div>

              <div
                style={{ marginTop: "16px", fontSize: "14px", color: "#666" }}
              >
                <p>
                  ⚠️ Please review the feedback above and update your listing
                  accordingly.
                </p>
              </div>
            </div>

            <div className="mytasks-modal-actions">
              <button
                className="mytasks-primary-button"
                onClick={closeRevisionModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const NotificationItem = ({ notification, onMarkRead }) => (
  <div
    className={`mytasks-notification-item ${
      notification.is_read
        ? "mytasks-notification-read"
        : "mytasks-notification-unread"
    }`}
    onClick={() => !notification.is_read && onMarkRead(notification.id)}
  >
    <div className="mytasks-notification-content">
      <p className="mytasks-notification-message">{notification.message}</p>
      {notification.task_title && (
        <p className="mytasks-notification-task">
          Task: {notification.task_title}
        </p>
      )}
      <p className="mytasks-notification-time">
        {new Date(notification.created_at).toLocaleString()}
      </p>
    </div>
    {!notification.is_read && <div className="mytasks-notification-dot"></div>}
  </div>
);

export default MyTasksPage;
